import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import {
  Plus, Edit, Trash2, Eye, EyeOff, ChevronLeft, ChevronRight,
  Settings, Upload, AlertTriangle, UserPlus, User, List, X, Phone, Mail,
  Share2, Copy, Link, RotateCcw, ExternalLink, Download, QrCode, Ticket,
  Users, CheckCircle, XCircle, Wine, MessageCircle, RefreshCw
} from "lucide-react";
import { API, BACKEND_URL } from "@/config/api";
import PasswordInput from "@/components/common/PasswordInput";

const ScannerUsersTab = ({ credentials, events, fetchEvents, isGestor, gestorPrivileges, isSuperuser, setActiveTab }) => {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [scannerUsers, setScannerUsers] = useState([]);
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    username: "",
    password: "",
    nombre: "",
    email: "",
    activo: true
  });

  const fetchScannerUsers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/scanner-users`, {
        headers: { Authorization: `Basic ${credentials}` }
      });
      setScannerUsers(response.data);
    } catch (e) {
      console.error("Error fetching scanner users:", e);
    }
  }, [credentials]);

  const handleUserFormChange = (e) => {
    const { name, value } = e.target;
    setUserForm(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmitUser = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const config = { headers: { Authorization: `Basic ${credentials}` } };

      if (editingUser) {
        const updateData = { 
          username: userForm.username,
          nombre: userForm.nombre,
          email: userForm.email,
          activo: userForm.activo
        };
        if (userForm.password) {
          updateData.password = userForm.password;
        }
        await axios.put(`${API}/admin/scanner-users/${editingUser.id}`, updateData, config);
      } else {
        await axios.post(`${API}/admin/scanner-users`, userForm, config);
      }

      setShowUserForm(false);
      setEditingUser(null);
      setUserForm({ username: "", password: "", nombre: "", email: "", activo: true });
      fetchScannerUsers();
    } catch (e) {
      setError(e.response?.data?.detail || "Error al guardar el usuario");
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserForm({
      username: user.username,
      password: "",
      nombre: user.nombre,
      email: user.email || "",
      activo: user.activo !== false
    });
    setShowUserForm(true);
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("¿Estás seguro de eliminar este usuario?")) return;
    
    try {
      await axios.delete(`${API}/admin/scanner-users/${userId}`, {
        headers: { Authorization: `Basic ${credentials}` }
      });
      fetchScannerUsers();
    } catch (e) {
      alert("Error al eliminar el usuario");
    }
  };

  const handleToggleUserActive = async (user) => {
    try {
      await axios.put(`${API}/admin/scanner-users/${user.id}`, 
        { activo: !user.activo },
        { headers: { Authorization: `Basic ${credentials}` } }
      );
      fetchScannerUsers();
    } catch (e) {
      alert("Error al actualizar el usuario");
    }
  };

  useEffect(() => {
    fetchScannerUsers();
  }, [fetchScannerUsers]);

  return (
    <>
          <>
            {/* Add User Button */}
            {!showUserForm && (
              <button 
                onClick={() => setShowUserForm(true)}
                className="btn-primary mb-8 w-auto px-6"
                data-testid="add-user-btn"
              >
                <UserPlus size={18} className="inline mr-2" />
                Nuevo Usuario
              </button>
            )}

            {/* User Form */}
            {showUserForm && (
              <div className="form-section mb-8 max-w-md">
                <h3 className="form-title mb-6">
                  {editingUser ? "Editar Usuario Escáner" : "Nuevo Usuario Escáner"}
                </h3>
                
                {error && <div className="error-message mb-4">{error}</div>}
                
                <form onSubmit={handleSubmitUser}>
                  <div className="form-group">
                    <label className="form-label">Nombre de Usuario</label>
                    <input
                      type="text"
                      name="username"
                      className="custom-input"
                      value={userForm.username}
                      onChange={handleUserFormChange}
                      required
                      data-testid="user-username"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">
                      {editingUser ? "Nueva Contraseña (dejar vacío para mantener)" : "Contraseña"}
                    </label>
                    <PasswordInput
                      name="password"
                      value={userForm.password}
                      onChange={handleUserFormChange}
                      required={!editingUser}
                      minLength={6}
                      testId="user-password"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Nombre Completo</label>
                    <input
                      type="text"
                      name="nombre"
                      className="custom-input"
                      value={userForm.nombre}
                      onChange={handleUserFormChange}
                      required
                      data-testid="user-nombre"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      name="email"
                      className="custom-input"
                      placeholder="usuario@ejemplo.com"
                      value={userForm.email}
                      onChange={handleUserFormChange}
                      data-testid="user-email"
                    />
                  </div>
                  
                  {editingUser && (
                    <div className="form-group">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={userForm.activo}
                          onChange={(e) => setUserForm(prev => ({ ...prev, activo: e.target.checked }))}
                          className="w-5 h-5 accent-[var(--primary)]"
                          data-testid="user-activo"
                        />
                        <span className="text-white">Usuario Activo</span>
                      </label>
                      <p className="text-xs text-[var(--text-muted)] mt-1">
                        Los usuarios inactivos no pueden iniciar sesión
                      </p>
                    </div>
                  )}
                  
                  <div className="flex gap-4 mt-6">
                    <button type="submit" className="btn-primary" disabled={loading} data-testid="user-submit-btn">
                      {loading ? <span className="spinner"></span> : (editingUser ? "Guardar Cambios" : "Crear Usuario")}
                    </button>
                    <button 
                      type="button" 
                      onClick={() => {
                        setShowUserForm(false);
                        setEditingUser(null);
                        setUserForm({ username: "", password: "", nombre: "", email: "", activo: true });
                        setError("");
                      }}
                      className="btn-download"
                    >
                      Cancelar
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Users List */}
            <div className="admin-events-list">
              <h3 className="font-heading text-xl text-white mb-4 uppercase">
                Usuarios del Escáner ({scannerUsers.length})
              </h3>
              
              {scannerUsers.length === 0 ? (
                <div className="text-center py-8">
                  <User size={48} className="text-[var(--text-muted)] mx-auto mb-4" />
                  <p className="text-[var(--text-muted)]">No hay usuarios creados</p>
                  <p className="text-[var(--text-muted)] text-sm mt-2">
                    Crea usuarios para que puedan validar entradas en el escáner QR
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {scannerUsers.map(user => (
                    <div 
                      key={user.id} 
                      className={`admin-event-card ${!user.activo ? 'opacity-50' : ''}`}
                      data-testid={`admin-user-${user.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-[var(--primary)] flex items-center justify-center">
                            <User size={24} className="text-black" />
                          </div>
                          <div>
                            <h4 className="text-white font-bold">{user.nombre}</h4>
                            <p className="text-[var(--text-muted)] text-sm">@{user.username}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          <button
                            onClick={() => handleToggleUserActive(user)}
                            className={`text-xs px-3 py-1 ${user.activo 
                              ? 'bg-[var(--success)] text-black' 
                              : 'bg-[var(--error)] text-white'}`}
                            data-testid={`toggle-user-${user.id}`}
                          >
                            {user.activo ? 'Activo' : 'Inactivo'}
                          </button>
                          <button 
                            onClick={() => handleEditUser(user)}
                            className="p-2 text-[var(--text-muted)] hover:text-[var(--primary)] transition-colors"
                            data-testid={`edit-user-${user.id}`}
                          >
                            <Edit size={18} />
                          </button>
                          <button 
                            onClick={() => handleDeleteUser(user.id)}
                            className="p-2 text-[var(--text-muted)] hover:text-[var(--error)] transition-colors"
                            data-testid={`delete-user-${user.id}`}
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>

    </>
  );
};

export default ScannerUsersTab;
